import {SortOrderEnum} from './Common.types'

/** Microsoft consent type: AllPrincipals (all users) or Principal (specific user) */
export type MicrosoftConsentType = 'AllPrincipals' | 'Principal'

/** How consent was granted */
export enum SSPMAppConsentMethodEnum {
  Admin = 'admin',
  User = 'user',
  Combined = 'combined', // both admin and user consent
}

/** Dope categorization of app access scope */
export enum SSPMAppCategoryEnum {
  Global = 'global', // organization-wide
  Scoped = 'scoped', // limited access
  Login = 'login', // identity-only
}

/** Risk level assessment for the app */
export enum SSPMAppRiskLevelEnum {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Critical = 'critical',
}

/** Telemetry data availability for this app in the analysis window. */
export enum SSPMTelemetryAvailability {
  Available = 'available',
  Partial = 'partial',
  Unavailable = 'unavailable',
}

/** Activity posture derived from user/app sign-in recency. */
export enum SSPMAppActivityStatus {
  Active = 'active',
  Inactive = 'inactive',
  Unknown = 'unknown',
}

/**
 * Permission usage posture over the telemetry analysis window.
 * - rightsized: the granted permission set mostly matches observed usage, with little or no excess.
 * - overprovisioned: the app has materially more access than needed.
 * - unknown: usage data is insufficient to determine posture.
 */
export enum SSPMPermissionUsageStatus {
  Rightsized = 'rightsized',
  Overprovisioned = 'overprovisioned',
  Unknown = 'unknown',
}

/** App level permissions */
export interface AppRole {
  /** The MS unique id for this app role */
  msAppRoleId: string

  /** The name of the resource the permission is against, e.g. Microsoft Graph */
  resourceName: string

  /** The uuid of the resource, e.g. 25d76413-902c-47c2-8a3a-413b12cdea24 */
  resourceId: string | null

  /** The permission granted, e.g. User.Read.All */
  permission: string

  /** The description of the granted permission, e.g. "Read all users' full profiles" */
  description: string
}

/** Application User data */
export interface SSPMUserData {
  /** The user id */
  msUserId: string

  /** The user display name */
  displayName: string

  /** The user email address */
  email: string
}

/** Delegated grant permissions */
export interface DelegatedGrant {
  /** The name of the resource the permission is against, e.g. Microsoft Graph */
  resourceName: string

  /** Indicates if the consent is for all or a specific principal */
  consentType: MicrosoftConsentType

  /** The user data for the principal, optional if the consentType is AllPrincipals */
  userData?: SSPMUserData

  /** The list of scopes granted, e.g. "openid profile User.Read offline_access Files.ReadWrite" */
  scopes: string
}

/** Publisher information */
export interface PublisherInfo {
  /** Verified publisher */
  verified: boolean

  /** Publisher name */
  publisherName?: string

  /** URL to the app's logo image */
  logoUrl?: string

  /** URL to the app's homepage */
  homepageUrl?: string

  /** URL to the app's terms of service */
  termsOfServiceUrl?: string

  /** URL to the app's privacy statement */
  privacyStatementUrl?: string
}

/** Gallery App information */
export interface GalleryAppInfo {
  /** Entra App Gallery app name - may differ from customers set app display name */
  displayName: string

  /** Entra App Gallery provided description for app */
  description: string

  /** Entra App Gallery categories */
  categories: string[]
}

/** SSO mode for login apps */
export type SSOMode = 'saml' | 'oidc' | 'password' | 'none'

/** Login app specific information */
export interface LoginInfo {
  /** The SSO mode for logging in */
  ssoMode: SSOMode

  /** Indicates if the app supports automatic provisioning of users i.e. scim */
  provisionUsers: boolean
}

/** Information about the organization that owns the app */
export interface OrganizationData {
  /** The organization's MS tenant id */
  msTenantId: string
}

/** Permission usage summary over the analysis window */
export interface SSPMPermissionSummary {
  /** Permission usage posture over the lookback window */
  usageStatus: SSPMPermissionUsageStatus

  /** Count of granted permissions, across both app permissions and delegated grants. */
  grantedCount: number

  /** Count of granted permissions with no observed (resource level) usage during the analysis window */
  unusedCount: number
}

/** Sign-in telemetry summary over the analysis window */
export interface SSPMSignInSummary {
  /** Most recent observed sign-in for the app (user or service principal) */
  lastSignInDateTime?: string

  /** Successful user (delegated) sign-ins. Only present when the app has delegated grants. */
  userSuccessCount?: number

  /** Failed user (delegated) sign-ins. Only present when the app has delegated grants. */
  userFailedCount?: number

  /** Successful application-only sign-ins. Only present when the app has application permissions. */
  applicationSuccessCount?: number

  /** Failed application-only sign-ins. Only present when the app has application permissions. */
  applicationFailedCount?: number

  /** List of country codes (two letter code) where the sign-in originated */
  countries?: string[]
}

/** Compact telemetry summary for app list triage */
export interface MicrosoftAppTelemetrySummary {
  /** Snapshot timestamp the telemetry summary is derived from */
  asOfDateTime: string

  /** Number of days included in telemetry analysis */
  lookbackDays: number

  /** Availability of telemetry data for this app */
  availability: SSPMTelemetryAvailability

  /** Derived activity status for this app */
  activityStatus?: SSPMAppActivityStatus

  /** Permission usage summary for the app over the analysis window */
  permission?: SSPMPermissionSummary

  /** Sign-in telemetry for the app over the analysis window */
  signIn?: SSPMSignInSummary
}

export interface MicrosoftAppListItem {
  /** MS unique id for this app instance */
  msObjectId: string

  /** MS unique id for this app */
  msAppId: string

  /** Display name of the application */
  displayName: string

  /** The date and time the app was created in Microsoft for the customer (ISO 8601 format) */
  createdDateTime?: string

  /** The date and time the app was last synchronised with SSPM (ISO 8601 format) */
  lastSyncDateTime: string

  /** The user who created (or first authorized) the app */
  creator?: SSPMUserData

  /** Dopamine AI-generated analysis of the app */
  dopamine: string

  /** Information from the publisher of the app */
  publisherInfo: PublisherInfo

  /** Information about the MS tenant that originated the app */
  appOwnerOrganization: OrganizationData

  /** The enablement status of the app */
  appEnabled: boolean

  /** Indicates if the app has been disabled by Microsoft due to DisabledDueToViolationOfServicesAgreement */
  disabledByMicrosoft: boolean

  /** Owners assigned to the app */
  owners: SSPMUserData[]

  /** Reply (redirect) URLs registered for this application - can be empty */
  replyUrls: string[]

  /** Dope overall categorization of the app */
  category: SSPMAppCategoryEnum

  /** Dope categorization of the consent method used for the app */
  consentMethod: SSPMAppConsentMethodEnum

  /** Overall risk level categorization for the app */
  riskLevel: SSPMAppRiskLevelEnum

  /** Optional compact telemetry summary for list-level triage */
  telemetrySummary?: MicrosoftAppTelemetrySummary

  /** Is this app created "in-house" or from an external provider */
  selfPublished: boolean

  /** Is this app single tenant or multi-tenant */
  singleTenant: boolean

  /** Optional information if this app is a login app */
  loginInfo?: LoginInfo

  /** Optional information if this app is from the MS Entra Gallery */
  galleryAppInfo?: GalleryAppInfo
}

/** Applications list type */
export type SSPMApplicationsListType = MicrosoftAppListItem[]

/** A recommended call to action highlighting an app that warrants attention */
export interface SSPMAppCallToAction {
  /** Display name of the application, when available */
  displayName?: string

  /** MS unique id for the app instance this call to action refers to */
  msObjectId: string

  /** MS unique id for the app this call to action refers to */
  msAppId: string

  /** Short title for the call to action */
  title: string

  /** 1–2 sentence action summary */
  summary: string

  /** Overall risk level categorization */
  riskLevel: SSPMAppRiskLevelEnum
}

/** Response containing Microsoft/Google third-party apps with pagination */
export interface SSPMApplicationsAPIResponse {
  /** Array of Microsoft third-party applications */
  apps: SSPMApplicationsListType

  /** Cursor-based pagination information */
  pageInfo: SSPMApplicationsPageInfo
}

export interface SSPMApplicationsAPIResponseData {
  data: SSPMApplicationsAPIResponse
}

/** Response containing app details including roles and delegated grants with pagination */
export interface SSPMAppDetailsResponse {
  /** Microsoft unique id for this app */
  msObjectId: string

  /** Display name of the application */
  appDisplayName: string

  /** App level permissions assigned to the app - can be empty and not present in subsequent pages */
  appRoles?: AppRole[]

  /** Array of delegated permission grants - can be empty */
  delegatedGrants: DelegatedGrant[]

  /** Cursor-based pagination information - needed for additional pages of delegated grants */
  pageInfo: SSPMApplicationsPageInfo
}

export interface SSPMAppDetailsResponseData {
  data: SSPMAppDetailsResponse
}

export interface SSPMAppDetailsAPIParams {
  /** Cursor for the next page of delegated grants */
  after?: string
}

/** Overview of the SSPM module for the Microsoft tenant */
export interface SSPMOverviewMetricBucket {
  count: number
}

/** Overall security posture score for the tenant (0 = worst, 25 = best). */
export interface SSPMOverviewSecurityPosture {
  score: number
}

export interface SSPMOverviewSummary {
  /** Number of days included in summary analysis */
  analysisWindowDays: number

  /** Apps categorized as critical risk */
  criticalRiskApps: SSPMOverviewMetricBucket

  /** Apps categorized as high risk */
  highRiskApps: SSPMOverviewMetricBucket

  /** Apps with over-provisioned permissions posture */
  overProvisionedApps: SSPMOverviewMetricBucket

  /** Apps where publisher is not verified */
  unverifiedPublisherApps: SSPMOverviewMetricBucket

  /** Apps that do not have an owner */
  unownedApps: SSPMOverviewMetricBucket

  /** Apps that are stale/inactive */
  staleApps: SSPMOverviewMetricBucket

  /** Newly discovered apps */
  newApps: SSPMOverviewMetricBucket

  /** Overall security posture score, 0 (worst) to 25 (best). */
  securityPosture: SSPMOverviewSecurityPosture
}

export interface SSPMOverviewResponse {
  /** Total count of third-party apps discovered */
  totalApps: number

  /** Whether tenant license supports telemetry-based enrichment */
  entraLicenseSupportsTelem?: boolean

  /** Aggregated summary metrics for dashboard cards (always present from BE with every bucket). */
  summary: SSPMOverviewSummary

  /** Optional sorted (DESC) list of up to 10 recommended call to action entries */
  callToActions?: SSPMAppCallToAction[]

  /** Whether or not the initial onboarding scan is currently in progress */
  initialScanInProgress: boolean

  /** The last time the apps were synchronised with the Microsoft tenant */
  lastSyncDateTime?: string
}

export type SSPMOverviewMetricKey = keyof Pick<
  SSPMOverviewSummary,
  | 'criticalRiskApps'
  | 'highRiskApps'
  | 'overProvisionedApps'
  | 'unverifiedPublisherApps'
  | 'unownedApps'
  | 'staleApps'
  | 'newApps'
>

export interface SSPMOverviewAPIResponseData {
  data: SSPMOverviewResponse
}

/** Cursor-based pagination information */
export interface SSPMApplicationsPageInfo {
  /** Cursor for the next page */
  endCursor?: string

  /** Whether there are more results available */
  hasNextPage: boolean
}

/** Parameters for getting SSPM applications */
export interface SSPMApplicationsAPIParams {
  /**
   * Sort order for results. Format: "field:direction" or "field" (defaults to asc).
   * Allowed fields: createdDateTime, total sign-ins
   * Direction: asc or desc.
   */
  sort?: string

  /** Cursor for the next page */
  after?: string

  /** Limit the number of results to the first n items */
  first?: number

  /** Filter by risk level (comma-separated values) */
  riskLevel?: string

  /** Filter by consent method (comma-separated values) */
  consentMethod?: string

  /** Filter by activity status (comma-separated values) */
  activityStatus?: string

  /** Filter by permission usage posture (comma-separated values) */
  permissionUsageStatus?: string

  /** Filter by category (comma-separated values) */
  category?: string

  /**
   * General fuzzy search by application display name, publisher name or creator's email address (case-insensitive substring match).
   * Only one of the search query-params, `search`, `displayName`, `publisherName` or `creatorEmail` can be provided at a time.
   */
  search?: string

  /** Exact search by creator email address (case-insensitive substring match) */
  creatorEmail?: string

  /**
   * When `false`, returns only apps whose publisher is unverified.
   * When `true`, returns only apps from verified publishers.
   */
  publisherVerified?: boolean

  /**
   * When `false`, returns only apps with no assigned owners.
   * When `true`, returns only apps that have at least one owner.
   */
  hasOwner?: boolean

  /**
   * When `true`, returns apps created or first seen within the SSPM new-apps window.
   * When `false`, returns apps outside that window.
   */
  showNewApps?: boolean
}

export type AppliedFilters = {
  category: string[]
  riskLevel: string[]
  consent: string[]
  activityStatus: string[]
  permissionUsageStatus: string[]
  filterUnverifiedPublishers: boolean
  filterNoOwners: boolean
  filterNewApps: boolean
  search: string
  creatorEmail: string
  sort: string
  direction?: SortOrderEnum
}

/** Google SSPM uses the same assessed risk values as Microsoft, plus an unassessed state. */
export type GoogleSSPMAssessedRiskLevel = SSPMAppRiskLevelEnum
export type GoogleSSPMRiskLevel = GoogleSSPMAssessedRiskLevel | 'unavailable'

export type GoogleSSPMSort =
  | 'firstSeenAt:asc'
  | 'firstSeenAt:desc'
  | 'totalUserCount:asc'
  | 'totalUserCount:desc'

export interface GoogleSSPMApp {
  clientId: string
  displayName: string
  accessType: 'accessed'
  category: SSPMAppCategoryEnum
  riskLevel: GoogleSSPMRiskLevel
  dopamine: string
  totalUserCount: number
  uniqueScopeCount: number
  firstSeenAt: string
  lastSeenAt: string
}

export interface GoogleSSPMScope {
  scope: string
  userCount: number
}

export interface GoogleSSPMAppDetails {
  clientId: string
  displayName: string
  scopes: GoogleSSPMScope[]
}

export interface GoogleSSPMPageInfo {
  hasNextPage: boolean
  endCursor: string | null
}

export interface GoogleSSPMApplicationsResponse {
  apps: GoogleSSPMApp[]
  pageInfo: GoogleSSPMPageInfo
}

export interface GoogleSSPMApplicationsAPIResponseData {
  data: GoogleSSPMApplicationsResponse
}

export interface GoogleSSPMAppDetailsAPIResponseData {
  data: GoogleSSPMAppDetails
}

export interface GoogleSSPMOverviewMetricBucket {
  count: number
}

export interface GoogleSSPMOverviewSummary {
  criticalRiskApps: GoogleSSPMOverviewMetricBucket
  highRiskApps: GoogleSSPMOverviewMetricBucket
  mediumRiskApps: GoogleSSPMOverviewMetricBucket
  lowRiskApps: GoogleSSPMOverviewMetricBucket
  newApps: GoogleSSPMOverviewMetricBucket
  securityPosture: SSPMOverviewSecurityPosture
}

export interface GoogleSSPMCallToAction {
  displayName?: string
  clientId: string
  title: string
  summary: string
  riskLevel: GoogleSSPMAssessedRiskLevel
}

export interface GoogleSSPMOverviewResponse {
  totalApps: number
  totalUsersWithGrants: number
  directoryUserCount: number
  summary: GoogleSSPMOverviewSummary
  callToActions: GoogleSSPMCallToAction[]
  initialScanInProgress?: boolean
  lastSyncDateTime?: string
}

export interface GoogleSSPMOverviewAPIResponseData {
  data: GoogleSSPMOverviewResponse
}

export interface GoogleSSPMApplicationsAPIParams {
  first?: number
  after?: string
  /** One comma-separated category value. */
  category?: string
  riskLevel?: GoogleSSPMAssessedRiskLevel[]
  showNewApps?: boolean
  search?: string
  sort?: GoogleSSPMSort
}
